import { useState, useMemo, useEffect, useRef } from 'react';
import { RevoltGlassButton, RevoltSwitch, RevoltIcon, RevoltTooltip } from '../../../components';
import { parseMarkdown, setupCodeBlockCopyButtons, setupExternalLinkHandlers, renderMath } from '../../../core';
import './ShareCard.css';

const REVIEWABLE_STATUSES = new Set(['pending', 'approved', 'rejected', 'deferred']);

function formatReviewStatusLabel(status) {
  switch (status) {
    case 'pending':
      return 'Pending';
    case 'approved':
      return 'Approved';
    case 'rejected':
      return 'Rejected';
    case 'deferred':
      return 'Deferred';
    default:
      return status || 'Review';
  }
}

function getReviewRequirementReason(reviewBypassSource) {
  switch (reviewBypassSource) {
    case 'app_force_review':
      return 'the app has a manual review override enabled';
    case 'user_force_review':
      return 'the account has a manual review override enabled';
    case 'free_default':
      return 'the app is published from a free account';
    case 'paid_default':
      return 'the app matches review criteria that require a manual check';
    default:
      return 'the app requires a manual review';
  }
}

function getReviewTooltipContent(status, reviewBypassSource) {
  const reason = getReviewRequirementReason(reviewBypassSource);

  switch (status) {
    case 'pending':
      return {
        title: 'Pending review',
        body: `This app needs manual review because ${reason}. It is currently waiting for a decision before it can be fully approved for community publishing.`,
        note: 'If the app changes while waiting, a new review request may be created.',
      };
    case 'approved':
      return {
        title: 'Approved',
        body: `This app needed manual review because ${reason} and has been approved.`,
        note: 'Any qualifying changes to the app will require a new review.',
      };
    case 'rejected':
      return {
        title: 'Rejected',
        body: `This app needed manual review because ${reason} and was not approved.`,
        note: 'Make your changes, then sync to submit for review again.',
      };
    case 'deferred':
      return {
        title: 'Deferred',
        body: `This app needed manual review because ${reason}, but there was not enough information to approve or reject it yet.`,
        note: 'Sync your changes to submit a new version for review.',
      };
    default:
      return null;
  }
}

export function ShareCard({ app, canManage = false, isMember = false, isPublicViewer = false, detailedViewInfoEnabled = false, onTogglePublic, onToggleRemixing, onTogglePublish }) {
  const [copiedShare, setCopiedShare] = useState(false);
  const [copiedEmbed, setCopiedEmbed] = useState(false);
  const [copiedFullscreen, setCopiedFullscreen] = useState(false);
  const [includeHeader, setIncludeHeader] = useState(true);
  const [optimisticRemixing, setOptimisticRemixing] = useState(app?.allowRemixing);
  const [optimisticPublish, setOptimisticPublish] = useState(
    app?.isPublishedToCommunity === true || (app?.isPublishedToCommunity === undefined && app?.isPublic)
  );
  const reviewBadgeRef = useRef(null);
  const reviewNotesRef = useRef(null);
  const [isReviewTooltipOpen, setIsReviewTooltipOpen] = useState(false);

  const parsedReviewNotes = useMemo(() => {
    const notes = app?.community?.reviewNotes?.trim() || null;
    return notes ? parseMarkdown(notes) : '';
  }, [app?.community?.reviewNotes]);

  useEffect(() => {
    const notes = app?.community?.reviewNotes?.trim() || null;
    if (reviewNotesRef.current && notes && isReviewTooltipOpen) {
      setupCodeBlockCopyButtons(reviewNotesRef.current);
      setupExternalLinkHandlers(reviewNotesRef.current);
      renderMath(reviewNotesRef.current);
    }
  }, [app?.community?.reviewNotes, isReviewTooltipOpen]);

  // Update optimistic state when app prop changes
  useEffect(() => {
    setOptimisticRemixing(app?.allowRemixing);
  }, [app?.allowRemixing]);

  useEffect(() => {
    setOptimisticPublish(
      app?.isPublishedToCommunity === true || (app?.isPublishedToCommunity === undefined && app?.isPublic)
    );
  }, [app?.isPublishedToCommunity, app?.isPublic]);

  // Handle optimistic publish toggle
  const handlePublishToggle = async () => {
    if (!canManage || !onTogglePublish) return;

    const newValue = !optimisticPublish;
    setOptimisticPublish(newValue);

    try {
      await onTogglePublish(newValue);
    } catch (error) {
      setOptimisticPublish(!newValue);
    }
  };

  // Handle optimistic remixing toggle
  const handleRemixingToggle = async () => {
    if (!canManage || !onToggleRemixing) return;

    // Immediately update optimistic switch state
    const newValue = !optimisticRemixing;
    setOptimisticRemixing(newValue);

    try {
      // Call parent handler with the desired value
      await onToggleRemixing(newValue);
      // If successful, the app state will be updated by parent, which will update our optimisticRemixing via useEffect
    } catch (error) {
      // Revert optimistic switch state on error
      setOptimisticRemixing(!newValue);
    }
  };

  // Embed code - dynamically includes hideHeader parameter based on toggle
  // Must be called before early return to comply with React Hooks rules
  const embedCode = useMemo(() => {
    if (!app?.id) return '';
    const baseUrl = `${window.location.origin}/playspace/${app.id}?mode=embed`;
    const url = includeHeader ? baseUrl : `${baseUrl}&hideHeader=true`;
    return `<iframe src="${url}" width="800" height="600" frameborder="0" allowfullscreen style="border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);"></iframe>`;
  }, [app?.id, includeHeader]);

  const shareUrl = useMemo(() => {
    if (!app?.id) return '';
    return `${window.location.origin}/playspace/${app.id}?mode=sharing`;
  }, [app?.id]);

  const fullscreenUrl = useMemo(() => {
    if (!app?.id) return '';
    return `${window.location.origin}/playspace/${app.id}?mode=embed&hideHeader=true`;
  }, [app?.id]);

  if (!app) return null;

  const views = {
    normal: app?.viewsNormal || 0,
    sharing: app?.viewsSharing || 0,
    embedHeader: app?.viewsEmbedHeader || 0,
    embedNoHeader: app?.viewsEmbedNoHeader || 0,
    settings: app?.viewsSettings || 0,
  };
  const viewsTotal = views.normal + views.sharing + views.embedHeader + views.embedNoHeader + views.settings;
  const likesTotal = app?.likes || 0;
  const showDetailedViews = !!isMember && !isPublicViewer && !!detailedViewInfoEnabled;
  const reviewStatus = app?.community?.reviewStatus || null;
  const reviewRequired = app?.community?.reviewRequired === true;
  const reviewBypassSource = app?.community?.reviewBypassSource || null;
  const reviewNotes = app?.community?.reviewNotes?.trim() || null;
  const shouldShowReviewBadge = reviewRequired && REVIEWABLE_STATUSES.has(reviewStatus);
  const reviewTooltipContent = shouldShowReviewBadge
    ? getReviewTooltipContent(reviewStatus, reviewBypassSource)
    : null;

  const copyToClipboard = async (text) => {
    try {
      // Try modern Clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        return successful;
      }
    } catch (err) {
      console.error('Failed to copy text: ', err);
      return false;
    }
  };

  const handleCopyShare = async () => {
    const success = await copyToClipboard(shareUrl);
    if (success) {
      setCopiedShare(true);
      setTimeout(() => setCopiedShare(false), 2000);
    }
  };

  const handleCopyEmbed = async () => {
    const success = await copyToClipboard(embedCode);
    if (success) {
      setCopiedEmbed(true);
      setTimeout(() => setCopiedEmbed(false), 2000);
    }
  };

  const handleCopyFullscreen = async () => {
    const success = await copyToClipboard(fullscreenUrl);
    if (success) {
      setCopiedFullscreen(true);
      setTimeout(() => setCopiedFullscreen(false), 2000);
    }
  };

  return (
    <div className="account-card settings-card share-card">
      <div className="card-header">
        <h2 className="card-title">
          <i className="fa-solid fa-share-nodes"></i> Sharing
        </h2>
      </div>
      <div className="card-body share-body">
        <div className="share-views-row">
          <div className="share-views-summary">
            <div className="share-stat">
              <RevoltIcon name="eye" size="sm" />
              <span className="share-stat-value">{viewsTotal}</span>
            </div>
            <div className="share-stat">
              <i className="fa-solid fa-thumbs-up"></i>
              <span className="share-stat-value">{likesTotal}</span>
            </div>
          </div>
          {showDetailedViews && (
            <div className="share-views-grid">
              <div className="share-view-chip" title="Direct app views">
                <span className="share-view-chip-label">Direct</span>
                <span className="share-view-chip-value">{views.normal}</span>
              </div>
              <div className="share-view-chip" title="Sharing mode views">
                <span className="share-view-chip-label">Sharing</span>
                <span className="share-view-chip-value">{views.sharing}</span>
              </div>
              <div className="share-view-chip" title="Embed with header">
                <span className="share-view-chip-label">Embed</span>
                <span className="share-view-chip-value">{views.embedHeader}</span>
              </div>
              <div className="share-view-chip" title="Embed fullscreen">
                <span className="share-view-chip-label">Fullscreen</span>
                <span className="share-view-chip-value">{views.embedNoHeader}</span>
              </div>
              <div className="share-view-chip" title="Settings page views">
                <span className="share-view-chip-label">Settings</span>
                <span className="share-view-chip-value">{views.settings}</span>
              </div>
            </div>
          )}
        </div>

        <div className="share-divider" />

        <div className="visibility-status-row">
          <div className="access-status">
            <div className={`access-dot ${app.isPublic ? 'public' : 'private'}`}></div>
            <div className="access-text">
              <div className="access-label">{app.isPublic ? 'Public' : 'Private'}</div>
              <div className="access-description">
                {app.isPublic ? 'Anyone can view and remix this app' : 'Only invited members can access this app'}
              </div>
            </div>
          </div>
          {canManage && (
            <RevoltGlassButton
              variant="ghost"
              size="sm"
              onClick={onTogglePublic}
            >
              {app.isPublic ? 'Make Private' : 'Make Public'}
            </RevoltGlassButton>
          )}
        </div>

        {app.isPublic && (
          <div className="visibility-status-row visibility-status-row--publish">
            <div className="access-status">
              <div className={`access-dot ${optimisticPublish ? 'public' : 'private'}`}></div>
              <div className="access-text">
                <div className="access-label">Publish to community</div>
                <div className="access-description">
                  Show this app on the community page
                </div>
              </div>
            </div>
            <div className="visibility-status-row__right">
              {shouldShowReviewBadge && (
                <>
                  <span
                    ref={reviewBadgeRef}
                    className={`share-review-badge share-review-badge--${reviewStatus}`}
                    onFocus={() => setIsReviewTooltipOpen(true)}
                    onBlur={() => setIsReviewTooltipOpen(false)}
                    tabIndex={0}
                    aria-label={`Review status: ${formatReviewStatusLabel(reviewStatus)}`}
                  >
                    {formatReviewStatusLabel(reviewStatus)}
                  </span>
                  <RevoltTooltip
                    isOpen={isReviewTooltipOpen}
                    onOpenChange={setIsReviewTooltipOpen}
                    anchorRef={reviewBadgeRef}
                    placement="left"
                    strictPlacement
                    maxWidth={320}
                    closeDelay={30}
                    interactive
                  >
                    <div className="revolt-tooltip-title">{reviewTooltipContent?.title}</div>
                    <div className="revolt-tooltip-body">
                      <div>{reviewTooltipContent?.body}</div>
                      {reviewNotes && (
                        <div className="share-review-tooltip-message">
                          <span className="share-review-tooltip-message-label">Reviewer&apos;s message:</span>
                          <div
                            ref={reviewNotesRef}
                            className="share-review-tooltip-message-text share-review-tooltip-message-text--markdown"
                            dangerouslySetInnerHTML={{ __html: parsedReviewNotes }}
                          />
                        </div>
                      )}
                      <div className="share-review-tooltip-note">{reviewTooltipContent?.note}</div>
                    </div>
                  </RevoltTooltip>
                </>
              )}
              {canManage && (
                <RevoltSwitch
                  checked={optimisticPublish}
                  onChange={handlePublishToggle}
                />
              )}
            </div>
          </div>
        )}

        {app.isPublic && (
          <div className="visibility-status-row">
            <div className="access-status">
              <div className={`access-dot ${app.allowRemixing ? 'public' : 'private'}`}></div>
              <div className="access-text">
                <div className="access-label">Remixing</div>
                <div className="access-description">
                  Allow others to create new apps based on this one
                </div>
              </div>
            </div>
            {canManage && (
              <RevoltSwitch
                checked={optimisticRemixing}
                onChange={handleRemixingToggle}
              />
            )}
          </div>
        )}

        {app.isPublic && (
          <>
            <div className="share-divider" />

            <div className="share-sections">
              <div className="share-section">
                <div className="account-card settings-card share-subpanel">
                  <div className="card-header">
                    <h4 className="share-section-title">Links</h4>
                  </div>
                  <div className="card-body share-section-content">
                  <div className="share-row">
                    <div className="share-info">
                      <div className="share-icon">
                        <i className="fa-solid fa-link"></i>
                      </div>
                      <div className="share-details">
                        <div className="share-title">Share Link</div>
                        <div className="share-description">Minimal sharing mode with clean interface</div>
                      </div>
                    </div>
                    <RevoltGlassButton
                      variant="ghost"
                      size="sm"
                      onClick={handleCopyShare}
                      className={copiedShare ? 'copied' : ''}
                    >
                      <i className={`fa-solid ${copiedShare ? 'fa-check' : 'fa-copy'}`}></i> {copiedShare ? 'Copied!' : 'Copy'}
                    </RevoltGlassButton>
                  </div>

                  <div className="share-row">
                    <div className="share-info">
                      <div className="share-icon">
                        <i className="fa-solid fa-expand"></i>
                      </div>
                      <div className="share-details">
                        <div className="share-title">Full Screen</div>
                        <div className="share-description">Direct link to full screen view without header</div>
                      </div>
                    </div>
                    <RevoltGlassButton
                      variant="ghost"
                      size="sm"
                      onClick={handleCopyFullscreen}
                      className={copiedFullscreen ? 'copied' : ''}
                    >
                      <i className={`fa-solid ${copiedFullscreen ? 'fa-check' : 'fa-copy'}`}></i> {copiedFullscreen ? 'Copied!' : 'Copy'}
                    </RevoltGlassButton>
                  </div>
                </div>
              </div>
              </div>

              <div className="share-section">
                <div className="account-card settings-card share-subpanel">
                  <div className="card-header">
                    <h4 className="share-section-title">Embed</h4>
                  </div>
                  <div className="card-body share-section-content">
                  <div className="share-row">
                    <div className="share-info">
                      <div className="share-icon">
                        <i className="fa-solid fa-code"></i>
                      </div>
                      <div className="share-details">
                        <div className="share-title">Embed Code</div>
                        <div className="share-description">HTML iframe code for embedding on websites</div>
                      </div>
                    </div>
                    <div className="share-embed-controls">
                      <RevoltSwitch
                        checked={includeHeader}
                        onChange={setIncludeHeader}
                        label={includeHeader ? 'Include Header' : 'Without Header'}
                      />
                      <RevoltGlassButton
                        variant="ghost"
                        size="sm"
                        onClick={handleCopyEmbed}
                        className={copiedEmbed ? 'copied' : ''}
                      >
                        <i className={`fa-solid ${copiedEmbed ? 'fa-check' : 'fa-copy'}`}></i> {copiedEmbed ? 'Copied!' : 'Copy'}
                      </RevoltGlassButton>
                    </div>
                  </div>
                </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}